import React, { useState, useMemo } from 'react';
import { ChevronRight, Mail, Phone, ExternalLink } from 'lucide-react';
import { Lead } from '../../../types';
import { getFaviconUrl, getHostname } from '../../../utils/leadPanelUtils';
import { isLinkedIn, isNotEmpty } from '../../../utils/validation';
import { LinkedInIcon } from '../../common/LinkedInIcon';

// Keys to exclude from enrichment columns (standard fields shown elsewhere)
const EXCLUDED_KEYS = ['id', 'name', 'Name', 'url', 'URL', 'website', 'Website', 'description', 'Description', 'highlight', 'highlights', 'favicon'];

interface ReasoningCriterion {
  criterion_text: string;
  reasoning_text: string;
  satisfied: 'yes' | 'no' | 'unclear';
  source_urls: (string | any)[];
}

interface MobileDatasetTableProps {
  leads: Lead[];
  onLeadClick: (lead: Lead) => void;
}

export const MobileDatasetTable: React.FC<MobileDatasetTableProps> = ({
  leads,
  onLeadClick,
}) => {
  const [expandedHeader, setExpandedHeader] = useState<number | null>(null);

  // Get criteria columns from reasoning keys
  const criteriaColumns = useMemo(() => {
    const reasoningKeys = leads.length > 0 ? Object.keys(leads[0].reasoning || {}) : [];
    return reasoningKeys.slice(0, 5);
  }, [leads]);

  // Get enrichment columns that aren't criteria or excluded
  const enrichmentColumns = useMemo(() => {
    if (leads.length === 0) return [];
    const enrichmentCols: string[] = [];

    // Get all reasoning keys (criteria columns) to exclude them
    const reasoningKeys = new Set<string>();
    for (const lead of leads.slice(0, 10)) {
      Object.keys(lead.reasoning || {}).forEach(key => reasoningKeys.add(key));
    }

    // Check first 10 leads for which data columns have data
    for (const lead of leads.slice(0, 10)) {
      const data = lead.data || {};
      for (const key of Object.keys(data)) {
        if (EXCLUDED_KEYS.includes(key)) continue;
        if (reasoningKeys.has(key)) continue;
        if (isNotEmpty(data[key]) && !enrichmentCols.includes(key)) {
          enrichmentCols.push(key);
        }
      }
    }
    return enrichmentCols;
  }, [leads]);

  const tableMinWidth = Math.max(360, 180 + criteriaColumns.length * 100 + enrichmentColumns.length * 90);

  return (
    <div className="pb-28" data-tour="mobile-table-view">
      <div className="overflow-x-auto no-scrollbar">
        <table className="w-full" style={{ minWidth: tableMinWidth }}>
          <thead className="sticky top-0 z-20">
            <tr className="bg-white border-b border-gray-200 shadow-sm">
              {/* Row number column */}
              <th className="sticky left-0 bg-white px-2 py-3.5 text-center text-[10px] font-semibold text-gray-400 z-30" style={{ width: 40, minWidth: 40 }}>
                #
              </th>
              {/* Name column */}
              <th
                className="sticky bg-white px-3 py-3.5 text-left text-[12px] font-semibold text-gray-700 z-30 border-r border-gray-100"
                style={{ left: 40, width: 150, minWidth: 150, maxWidth: 150, boxShadow: '4px 0 6px -2px rgba(0, 0, 0, 0.05)' }}
              >
                Name
              </th>
              {/* Criteria/Match columns */}
              {criteriaColumns.map((col, idx) => {
                const criterion = leads[0]?.reasoning?.[col] as ReasoningCriterion | undefined;
                const headerText = criterion?.criterion_text || col;
                const shortHeader = headerText.length > 12 ? headerText.slice(0, 12) + '…' : headerText;
                const isExpanded = expandedHeader === idx;
                const needsTruncation = headerText.length > 12;
                return (
                  <th
                    key={idx}
                    className={`py-3.5 text-left text-[12px] font-semibold bg-white text-gray-700 px-3 transition-all ${
                      isExpanded ? 'bg-gray-50' : ''
                    }`}
                    style={{
                      minWidth: isExpanded ? 180 : 100,
                      maxWidth: isExpanded ? 260 : 100,
                    }}
                  >
                    {needsTruncation ? (
                      <button
                        onClick={() => setExpandedHeader(isExpanded ? null : idx)}
                        className="text-left w-full"
                      >
                        <span className={`${isExpanded ? 'whitespace-normal leading-snug text-gray-900' : 'whitespace-nowrap text-gray-700'}`}>
                          {isExpanded ? headerText : shortHeader}
                        </span>
                      </button>
                    ) : (
                      <span className="whitespace-nowrap">{headerText}</span>
                    )}
                  </th>
                );
              })}
              {/* Enrichment columns */}
              {enrichmentColumns.map((col, idx) => {
                const displayName = col.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
                const isEmailCol = col.toLowerCase().includes('email');
                const isLinkedInCol = isLinkedIn(col);
                const isPhoneCol = col.toLowerCase().includes('phone');
                return (
                  <th
                    key={`enrich-${idx}`}
                    className="py-3.5 text-left text-[12px] font-semibold bg-white text-gray-700 px-3"
                    style={{ minWidth: 90, maxWidth: 140 }}
                  >
                    <div className="flex items-center gap-1.5">
                      {isEmailCol && <Mail className="w-3 h-3 text-gray-500" />}
                      {isLinkedInCol && <LinkedInIcon size={12} />}
                      {isPhoneCol && <Phone className="w-3 h-3 text-gray-500" />}
                      <span className="whitespace-nowrap truncate">{displayName}</span>
                    </div>
                  </th>
                );
              })}
              <th className="bg-white px-2 py-3.5 w-8" />
            </tr>
          </thead>
          <tbody>
            {leads.map((lead, index) => (
              <TableRow
                key={lead.id}
                lead={lead}
                index={index}
                criteriaColumns={criteriaColumns}
                enrichmentColumns={enrichmentColumns}
                expandedHeader={expandedHeader}
                onLeadClick={onLeadClick}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

interface TableRowProps {
  lead: Lead;
  index: number;
  criteriaColumns: string[];
  enrichmentColumns: string[];
  expandedHeader: number | null;
  onLeadClick: (lead: Lead) => void;
}

const TableRow: React.FC<TableRowProps> = ({
  lead,
  index,
  criteriaColumns,
  enrichmentColumns,
  expandedHeader,
  onLeadClick,
}) => {
  const data = lead.data || {};
  const reasoning = lead.reasoning || {};
  const primaryName = data.Name || data.name || Object.values(data)[0] || 'Unknown';
  const url = data.URL || data.url || data.Website || data.website || '';

  return (
    <tr
      onClick={() => onLeadClick(lead)}
      className="bg-white border-b border-gray-100 active:bg-gray-50"
    >
      {/* Row number */}
      <td className="sticky left-0 bg-white px-2 py-3.5 text-center z-10" style={{ width: 40, minWidth: 40 }}>
        <span className="text-[11px] font-medium text-gray-400">{index + 1}</span>
      </td>
      {/* Name cell */}
      <td
        className="sticky bg-white px-3 py-3.5 z-10 border-r border-gray-100"
        style={{ left: 40, width: 150, minWidth: 150, maxWidth: 150, boxShadow: '4px 0 6px -2px rgba(0, 0, 0, 0.05)' }}
      >
        <div className="flex items-center gap-2.5">
          {url && (
            <a
              href={url.startsWith('http') ? url : `https://${url}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="flex-shrink-0 active:opacity-60"
            >
              {isLinkedIn(url) ? (
                <LinkedInIcon size={18} />
              ) : (
                <img
                  src={getFaviconUrl(getHostname(url), 32)}
                  alt=""
                  className="w-[18px] h-[18px] rounded"
                  onError={(e: any) => {
                    e.target.onerror = null;
                    e.target.src = '';
                    e.target.className = 'hidden';
                  }}
                />
              )}
            </a>
          )}
          <div className="min-w-0 flex-1">
            <p className="text-[13px] font-medium text-gray-900 truncate" style={{ maxWidth: 100 }}>
              {primaryName}
            </p>
          </div>
        </div>
      </td>
      {/* Criteria/Match column cells */}
      {criteriaColumns.map((col, colIdx) => {
        const criterion = reasoning[col] as ReasoningCriterion | undefined;
        const isMatch = criterion?.satisfied === 'yes';
        const isExpanded = expandedHeader === colIdx;
        const isFirstCell = index === 0 && colIdx === 0;
        return (
          <td
            key={colIdx}
            className="px-3 py-3.5"
            style={{ minWidth: isExpanded ? 200 : 100 }}
            {...(isFirstCell ? { 'data-tour': 'mobile-match-cell' } : {})}
          >
            {criterion ? (
              <div className="flex items-center gap-2">
                <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${isMatch ? 'bg-emerald-500' : 'bg-gray-200'}`} />
                <span className={`text-[12px] font-medium ${isMatch ? 'text-emerald-700' : 'text-gray-400'}`}>
                  {isMatch ? 'Yes' : 'No'}
                </span>
              </div>
            ) : (
              <span className="text-[12px] text-gray-300">—</span>
            )}
          </td>
        );
      })}
      {/* Enrichment column cells */}
      {enrichmentColumns.map((col, colIdx) => {
        const value = data[col];
        const isEmailCol = col.toLowerCase().includes('email');
        const isLinkedInCol = isLinkedIn(col);
        const isPhoneCol = col.toLowerCase().includes('phone');

        return (
          <td
            key={`enrich-cell-${colIdx}`}
            className="px-3 py-3.5"
            style={{ minWidth: 90, maxWidth: 140 }}
          >
            {isNotEmpty(value) ? (
              isEmailCol ? (
                <a
                  href={`mailto:${value}`}
                  onClick={(e) => e.stopPropagation()}
                  className="flex items-center gap-1.5 text-[12px] text-gray-700 hover:text-gray-900 active:opacity-70"
                >
                  <Mail className="w-3 h-3 flex-shrink-0 text-gray-500" />
                  <span className="truncate">{value}</span>
                </a>
              ) : isLinkedInCol && typeof value === 'string' ? (
                <a
                  href={value.startsWith('http') ? value : `https://${value}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="flex items-center gap-1.5 text-[12px] text-blue-600 hover:text-blue-700 active:opacity-70"
                >
                  <LinkedInIcon size={12} />
                  <span className="truncate">Profile</span>
                  <ExternalLink className="w-2.5 h-2.5 flex-shrink-0" />
                </a>
              ) : isPhoneCol ? (
                <a
                  href={`tel:${value}`}
                  onClick={(e) => e.stopPropagation()}
                  className="flex items-center gap-1.5 text-[12px] text-gray-700 hover:text-gray-900 active:opacity-70"
                >
                  <Phone className="w-3 h-3 flex-shrink-0 text-gray-500" />
                  <span className="truncate">{value}</span>
                </a>
              ) : (
                <span className="text-[12px] text-gray-700 truncate block">{String(value)}</span>
              )
            ) : (
              <span className="text-[12px] text-gray-300">—</span>
            )}
          </td>
        );
      })}
      <td className="px-2 py-3.5">
        <ChevronRight className="w-4 h-4 text-gray-300" />
      </td>
    </tr>
  );
};
